import { useState } from 'react'
import { GraphQLError } from 'graphql'
import moment from 'moment'
import { ITrailerStatesInputEnum } from 'types/FMSGraphqlTypes'
import { IError, Maybe } from 'types/graphqlTypes'
import { FORMATS } from 'constants/datetime'
import { useUpdateTrailerMutation } from '../graphql/UpdateTrailerMutation'
import { IAssignmentSchema } from '../schema/AssignmentSchema'

const getErrors = (
  errors?: readonly GraphQLError[],
  responseErrors?: Pick<IError, 'message'>[]
) => {
  if (errors && errors.length > 0) {
    return errors.map(e => e.message).join('; ')
  }
  if (responseErrors && responseErrors.length > 0) {
    return responseErrors.map(e => e.message).join('; ')
  }
  return null
}

const getEmptyAt = (date: Maybe<string>, time: Maybe<string>) => {
  if (!date || !time) return undefined

  const momentTime = moment(time, FORMATS.militaryTimeFormat)
  return moment(date)
    .set({
      hours: momentTime.hours(),
      minutes: momentTime.minutes(),
    })
    .format(FORMATS.iso8601Format)
}

export const useEditTrailer = (trailerId: string) => {
  const [updateTrailer] = useUpdateTrailerMutation()
  const [errors, setErrors] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const editTrailer = async (formData: IAssignmentSchema) => {
    setLoading(true)
    let newErrors = null
    let emptiedAt: string | undefined

    if (formData.status.value === 'assigned_to_facility') {
      emptiedAt = getEmptyAt(formData.emptyTrailerArrivalDate, formData.emptyTrailerArrivalTime)
    } else if (formData.status.value === 'empty_at_delivery') {
      emptiedAt = getEmptyAt(formData.emptyTrailerReadyDate, formData.emptyTrailerReadyTime)
    }

    try {
      const { data, errors: graphQLErrors } = await updateTrailer({
        variables: {
          input: {
            /**
             * Given that the state if auto-generated sending this field on when it's empty_at_facility
             * is going to assigned again to empty at facility, and want we want is to being set as
             * empty at delivery
             */
            assignedFacilityUuid:
              formData.status.value !== 'empty_at_delivery'
                ? formData.activeFacility?.id
                : undefined,
            emptiedAt,
            marketplaceTrailerId: trailerId,
            note: formData.notes,
            shipmentId: formData.status.value === 'unassigned' ? null : formData.assignedLoad?.id,
            state: ['inactive', 'unassigned'].includes(formData.status.value)
              ? (formData.status.value as ITrailerStatesInputEnum)
              : undefined, // Only state that can be set explicitly is inactive or unassigned
          },
        },
        refetchQueries: ['TrailerDetails'],
      })

      const { updateTrailer: response } = data || {}
      const { errors: responseErrors } = response || {}

      newErrors = getErrors(graphQLErrors, responseErrors)
    } catch (e) {
      newErrors = 'There was an error updating trailer'
    }

    setLoading(false)
    setErrors(newErrors)
    return {
      errors: newErrors,
    }
  }

  return {
    errors,
    editTrailer,
    loading,
  }
}
